import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Navigate } from 'react-router';

import type { AccountRole, AdminUserCreate, AuthUser } from './api';
import { authenticationErrorMessage } from './errors';
import { useAuthentication } from './authState';
import { guided } from '../guidance/fieldHelpRegistryCore';
import { userFieldHelp } from '../guidance/fieldHelpRegistry';

const EMPTY_USER: AdminUserCreate = {
  display_name: '',
  email: '',
  is_active: true,
  password: '',
  role: 'planner',
};

function sortedUsers(users: AuthUser[]): AuthUser[] {
  return [...users].sort((left, right) => left.display_name.localeCompare(right.display_name));
}

export function UserManagementPage() {
  const { createUser, listUsers, session, setUserEnabled, updateUser } = useAuthentication();
  const [users, setUsers] = useState<AuthUser[]>([]);
  const [draft, setDraft] = useState<AdminUserCreate>(EMPTY_USER);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [busyUserId, setBusyUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await listUsers();
      setUsers(sortedUsers(page.items));
    } catch (reason) {
      setError(authenticationErrorMessage(reason, 'User accounts could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [listUsers]);

  useEffect(() => {
    if (session?.user.role !== 'admin') return;
    void load();
  }, [load, session?.user.role]);

  if (session?.user.role !== 'admin') return <Navigate replace to="/app" />;

  const activeAdministratorCount = users.filter((user) => user.is_active && user.role === 'admin').length;

  function replaceUser(updated: AuthUser) {
    setUsers((current) => sortedUsers(current.map((user) => (user.id === updated.id ? updated : user))));
  }

  async function submitCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setCreating(true);
    const payload = draft;
    setDraft((current) => ({ ...current, password: '' }));
    try {
      const created = await createUser(payload);
      setUsers((current) => sortedUsers([...current, created]));
      setDraft(EMPTY_USER);
      setNotice(created.display_name + ' can now sign in.');
    } catch (reason) {
      setError(authenticationErrorMessage(reason, 'The user account could not be created.'));
    } finally {
      setCreating(false);
    }
  }

  async function changeRole(user: AuthUser, role: AccountRole) {
    if (role === user.role) return;
    setBusyUserId(user.id);
    setError(null);
    setNotice(null);
    try {
      const updated = await updateUser(user.id, { role });
      replaceUser(updated);
      setNotice(updated.display_name + ' is now a ' + updated.role + '.');
    } catch (reason) {
      setError(authenticationErrorMessage(reason, 'The account role could not be changed.'));
    } finally {
      setBusyUserId(null);
    }
  }

  async function toggleEnabled(user: AuthUser) {
    setBusyUserId(user.id);
    setError(null);
    setNotice(null);
    try {
      const updated = await setUserEnabled(user.id, !user.is_active);
      replaceUser(updated);
      setNotice(updated.display_name + (updated.is_active ? ' has been enabled.' : ' has been disabled.'));
    } catch (reason) {
      setError(authenticationErrorMessage(reason, 'The account status could not be changed.'));
    } finally {
      setBusyUserId(null);
    }
  }

  return (
    <div className="page user-management">
      <header className="page-header user-management__header">
        <h1>Users</h1>
      </header>

      {error === null ? null : (
        <div className="auth-message auth-message--error" role="alert">
          {error}
        </div>
      )}
      {notice === null ? null : (
        <div className="auth-message auth-message--success" role="status">
          {notice}
        </div>
      )}

      <details className="card user-create">
        <summary>Add user</summary>
        <form className="user-create-form" onSubmit={(event) => void submitCreate(event)}>
          <label>
            <span>Display name</span>
            <input
              {...guided(userFieldHelp.displayName)}
              autoComplete="off"
              maxLength={200}
              name="display-name"
              required
              value={draft.display_name}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  display_name: event.target.value,
                }))
              }
            />
          </label>
          <label>
            <span>Email address</span>
            <input
              {...guided(userFieldHelp.email)}
              autoComplete="off"
              inputMode="email"
              maxLength={254}
              name="new-user-email"
              required
              type="email"
              value={draft.email}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  email: event.target.value,
                }))
              }
            />
          </label>
          <label>
            <span>Initial password</span>
            <input
              {...guided(userFieldHelp.password)}
              aria-describedby="new-user-password-requirements"
              autoComplete="new-password"
              minLength={15}
              name="new-user-password"
              required
              type="password"
              value={draft.password}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  password: event.target.value,
                }))
              }
            />
          </label>
          <p id="new-user-password-requirements" className="user-create-form__hint">
            At least 15 characters.
          </p>
          <label>
            <span>Role</span>
            <select
              {...guided(userFieldHelp.newRole)}
              name="new-user-role"
              value={draft.role}
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  role: event.target.value as AccountRole,
                }))
              }
            >
              <option value="planner">Planner</option>
              <option value="admin">Administrator</option>
            </select>
          </label>
          <label className="checkbox-label">
            <input
              {...guided(userFieldHelp.active)}
              checked={draft.is_active}
              name="new-user-active"
              type="checkbox"
              onChange={(event) =>
                setDraft((current) => ({
                  ...current,
                  is_active: event.target.checked,
                }))
              }
            />
            <span>Account enabled</span>
          </label>
          <button className="button button--primary" disabled={creating} type="submit">
            {creating ? 'Creating…' : 'Create user'}
          </button>
        </form>
      </details>

      <section className="card" aria-labelledby="workspace-users-heading">
        <div className="section-heading">
          <h2 id="workspace-users-heading">Accounts</h2>
          <button disabled={loading} type="button" onClick={() => void load()}>
            Refresh
          </button>
        </div>

        {loading ? (
          <p role="status">Loading users…</p>
        ) : users.length === 0 ? (
          <p>No users.</p>
        ) : (
          <div className="user-table-scroll">
            <table className="user-table">
              <thead>
                <tr>
                  <th scope="col">User</th>
                  <th scope="col">Role</th>
                  <th scope="col">Status</th>
                  <th scope="col">Access</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => {
                  const finalActiveAdministrator = user.is_active && user.role === 'admin' && activeAdministratorCount === 1;
                  const busy = busyUserId === user.id;
                  return (
                    <tr key={user.id}>
                      <th scope="row">
                        <strong>{user.display_name}</strong>
                        <small>{user.email}</small>
                        {user.id === session.user.id ? <em>You</em> : null}
                      </th>
                      <td>
                        <label className="visually-hidden" htmlFor={'role-' + user.id}>
                          Role for {user.display_name}
                        </label>
                        <select {...guided(userFieldHelp.existingRole, user.id)} aria-describedby={finalActiveAdministrator ? 'final-admin-' + user.id : undefined} disabled={busy || finalActiveAdministrator} id={'role-' + user.id} value={user.role} onChange={(event) => void changeRole(user, event.target.value as AccountRole)}>
                          <option value="planner">Planner</option>
                          <option value="admin">Administrator</option>
                        </select>
                      </td>
                      <td>
                        <span className={user.is_active ? 'status-chip' : 'status-chip status-chip--readonly'}>{user.is_active ? 'Enabled' : 'Disabled'}</span>
                        {finalActiveAdministrator ? (
                          <small className="user-table__protection" id={'final-admin-' + user.id}>
                            Final active administrator
                          </small>
                        ) : null}
                      </td>
                      <td>
                        <button disabled={busy || finalActiveAdministrator} aria-label={`${user.is_active ? 'Disable' : 'Enable'} ${user.display_name}`} type="button" onClick={() => void toggleEnabled(user)}>
                          {busy ? 'Saving…' : user.is_active ? 'Disable' : 'Enable'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
